import { MutableRefObject, useEffect, useState } from 'react'
import { Reference } from '../../../../firebase'
import { findGlossaryIdsByDisplays } from '../../../../models/glossary'
import { APP_NAME, MATHPEDIA_HOST } from '../../../../utils'

type Box = {
  type: string
  title: string | null
  label: string | null
  number: number
}

const boxList: Record<string, { ja: string }> = {
  conj: { ja: '予想' },
  axm: { ja: '公理' },
  def: { ja: '定義' },
  prop: { ja: '命題' },
  fml: { ja: '公式' },
  lem: { ja: '補題' },
  thm: { ja: '定理' },
  cor: { ja: '系' },
  prf: { ja: '証明' },
  ex: { ja: '例' },
  exc: { ja: '問題' },
  rem: { ja: '注意' },
}

const buildShortenedLinks = (
  elm: HTMLDivElement,
  glossaryIds: Map<string, string | undefined>,
  references?: Reference[]
) => {
  const boxes: Box[] = []
  const boxElements = elm.querySelectorAll('.box')
  boxElements.forEach((boxElement) => {
    const type = boxElement.getAttribute('data-type') ?? 'normal'
    const title = boxElement.getAttribute('data-title')
    const label = boxElement.getAttribute('data-label')
    boxes.push({
      type,
      title,
      label,
      number: boxes.filter((b) => b.type === type).length + 1,
    })
  })

  // 各短縮リンクを展開
  const shortenedLinkElements = elm.querySelectorAll('.shortened-link')
  shortenedLinkElements.forEach((shortenedLinkElement) => {
    const rawTitle = shortenedLinkElement.getAttribute('data-title')

    // 用語の解析
    const glossaryId = rawTitle !== null ? glossaryIds.get(rawTitle) : undefined
    /// mathpediaの公開が終わるまでMathlogは用語を展開しない
    if (glossaryId !== undefined && APP_NAME !== 'mathlog') {
      // shortenedLinkElement.setAttribute('target', `_blank`) // mathlogの時は外部リンクで開く
      shortenedLinkElement.setAttribute(
        'href',
        `${MATHPEDIA_HOST}/glossaries/${glossaryId}`
      )
      return
    }

    // 参考文献の解析
    if (references) {
      const referenceIndex = references.findIndex(
        (reference) => reference.label === rawTitle
      )
      if (referenceIndex !== -1) {
        const reference = references[referenceIndex]
        if (reference.label === undefined) return
        shortenedLinkElement.setAttribute('href', `#${reference.label}`)
        shortenedLinkElement.innerHTML = `[${referenceIndex + 1}]`
        return
      }
    }

    // ボックスの解析
    const box = boxes.find(
      (box) => box.title === rawTitle || box.label === rawTitle
    )
    const localizedBoxType = box ? boxList[box.type] : undefined
    if (box && localizedBoxType) {
      if (rawTitle !== null)
        shortenedLinkElement.setAttribute('href', `#${rawTitle}`)
      shortenedLinkElement.innerHTML = `${localizedBoxType.ja}${box.number}`
      return
    }

    shortenedLinkElement.setAttribute('class', 'text-black')
  })
}

// [[用語]] のリンク先の用語解説のID。見つからなかった用語も undefined として覚えておき、ページをまたいで使い回す
const glossaryIdsCache = new Map<string, string | undefined>()

const resolveGlossaryIds = async (titles: string[]) => {
  const unresolvedTitles = titles.filter(
    (title) => !glossaryIdsCache.has(title)
  )
  if (unresolvedTitles.length > 0) {
    const foundGlossaryIds = await findGlossaryIdsByDisplays(unresolvedTitles)
    unresolvedTitles.forEach((title) =>
      glossaryIdsCache.set(title, foundGlossaryIds.get(title))
    )
  }
  return new Map(titles.map((title) => [title, glossaryIdsCache.get(title)]))
}

export const useShortenedLinksBuilder = (
  ref: MutableRefObject<HTMLDivElement | null>,
  references?: Reference[]
) => {
  const [waitingBuild, setWaitingBuild] = useState(true)

  useEffect(() => {
    if (!waitingBuild) return
    if (!ref.current) throw new Error('ref is null.')
    setWaitingBuild(false)
    const element = ref.current
    // Mathlogは用語を展開しないので、用語解説を探さない
    if (APP_NAME === 'mathlog') {
      buildShortenedLinks(element, new Map(), references)
      return
    }
    const titles = Array.from(
      element.querySelectorAll('.shortened-link'),
      (shortenedLinkElement) => shortenedLinkElement.getAttribute('data-title')
    ).filter((title): title is string => title !== null)
    resolveGlossaryIds(titles)
      .then((glossaryIds) =>
        buildShortenedLinks(element, glossaryIds, references)
      )
      .catch((error) => {
        console.warn(error)
        buildShortenedLinks(element, new Map(), references)
      })
  }, [references, waitingBuild])

  const rebuild = () => setWaitingBuild(true)
  return { rebuild }
}
